
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function SimulatorPage() {
  const [country, setCountry] = useState('MX');
  const [creditType, setCreditType] = useState('personal');
  const [amount, setAmount] = useState(10000);
  const [term, setTerm] = useState(24);
  const [showResults, setShowResults] = useState(false);

  const creditTypes = {
    personal: { name: 'Crédito Personal', rate: 0.12, minAmount: 1000, maxAmount: 50000, minTerm: 12, maxTerm: 60 },
    micro: { name: 'Microcrédito', rate: 0.15, minAmount: 500, maxAmount: 5000, minTerm: 3, maxTerm: 12 },
    business: { name: 'Financiamiento Empresarial', rate: 0.10, minAmount: 10000, maxAmount: 500000, minTerm: 12, maxTerm: 84 },
    mortgage: { name: 'Crédito Hipotecario', rate: 0.08, minAmount: 20000, maxAmount: 500000, minTerm: 60, maxTerm: 360 }
  };

  const currencies = {
    CL: { symbol: '$', name: 'CLP', multiplier: 900 },
    PE: { symbol: 'S/', name: 'PEN', multiplier: 3.7 },
    MX: { symbol: '$', name: 'MXN', multiplier: 17 }
  };

  const calculatePayment = () => {
    const selectedCredit = creditTypes[creditType as keyof typeof creditTypes];
    const monthlyRate = selectedCredit.rate / 12;
    const payment = amount * (monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1);
    const totalPayment = payment * term;
    const totalInterest = totalPayment - amount;
    
    return {
      monthlyPayment: payment,
      totalPayment: totalPayment,
      totalInterest: totalInterest,
      rate: selectedCredit.rate * 100
    };
  };

  const handleSimulate = () => {
    setShowResults(true);
  };

  const results = showResults ? calculatePayment() : null;
  const selectedCredit = creditTypes[creditType as keyof typeof creditTypes];
  const currency = currencies[country as keyof typeof currencies];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="pt-32 pb-24 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-800 mb-6">
              Simulador de Crédito
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Calcula tu cuota mensual y conoce las condiciones de tu crédito
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-3">País</label>
                <select 
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                >
                  <option value="CL">🇨🇱 Chile</option>
                  <option value="PE">🇵🇪 Perú</option>
                  <option value="MX">🇲🇽 México</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-3">Tipo de Crédito</label>
                <select 
                  value={creditType}
                  onChange={(e) => {
                    setCreditType(e.target.value);
                    const newCredit = creditTypes[e.target.value as keyof typeof creditTypes];
                    setAmount(newCredit.minAmount);
                    setTerm(newCredit.minTerm);
                    setShowResults(false);
                  }}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                >
                  <option value="personal">Crédito Personal</option>
                  <option value="micro">Microcrédito</option>
                  <option value="business">Financiamiento Empresarial</option>
                  <option value="mortgage">Crédito Hipotecario</option>
                </select>
              </div>
            </div>

            <div className="mb-8">
              <label className="block text-sm font-semibold text-slate-800 mb-3">
                Monto Solicitado: {currency.symbol}{amount.toLocaleString()} USD
              </label>
              <input 
                type="range"
                min={selectedCredit.minAmount}
                max={selectedCredit.maxAmount}
                step={selectedCredit.minAmount >= 10000 ? 1000 : 100}
                value={amount}
                onChange={(e) => {
                  setAmount(Number(e.target.value));
                  setShowResults(false);
                }}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-2">
                <span>{currency.symbol}{selectedCredit.minAmount.toLocaleString()}</span>
                <span>{currency.symbol}{selectedCredit.maxAmount.toLocaleString()}</span>
              </div>
            </div>

            <div className="mb-8">
              <label className="block text-sm font-semibold text-slate-800 mb-3">
                Plazo: {term} meses
              </label>
              <input 
                type="range"
                min={selectedCredit.minTerm}
                max={selectedCredit.maxTerm}
                step={creditType === 'mortgage' ? 12 : 1}
                value={term}
                onChange={(e) => {
                  setTerm(Number(e.target.value));
                  setShowResults(false);
                }}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-2">
                <span>{selectedCredit.minTerm} meses</span>
                <span>{selectedCredit.maxTerm} meses</span>
              </div>
            </div>

            <button 
              onClick={handleSimulate}
              className="w-full py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Calcular Cuota Mensual
            </button>

            {showResults && results && (
              <div className="mt-8 pt-8 border-t border-slate-200">
                <h3 className="text-2xl font-bold text-slate-800 mb-6 text-center">Resultados de la Simulación</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 text-center">
                    <p className="text-sm text-slate-600 mb-2">Cuota Mensual</p>
                    <p className="text-3xl font-bold text-emerald-700">
                      {currency.symbol}{results.monthlyPayment.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">USD</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-6 text-center">
                    <p className="text-sm text-slate-600 mb-2">Total a Pagar</p>
                    <p className="text-3xl font-bold text-slate-800">
                      {currency.symbol}{results.totalPayment.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">USD</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-6 text-center">
                    <p className="text-sm text-slate-600 mb-2">Intereses Totales</p>
                    <p className="text-3xl font-bold text-slate-800">
                      {currency.symbol}{results.totalInterest.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">USD</p>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-6 mb-6">
                  <h4 className="font-bold text-slate-800 mb-4">Detalles del Crédito</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Tipo de Crédito:</span>
                      <span className="font-semibold text-slate-800">{selectedCredit.name}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Monto Solicitado:</span>
                      <span className="font-semibold text-slate-800">{currency.symbol}{amount.toLocaleString()} USD</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Plazo:</span>
                      <span className="font-semibold text-slate-800">{term} meses</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Tasa de Interés Anual:</span>
                      <span className="font-semibold text-slate-800">{results.rate}%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
                  <div className="flex items-start space-x-3">
                    <i className="ri-information-fill text-amber-600 text-xl mt-0.5"></i>
                    <div>
                      <p className="text-sm text-amber-900 font-semibold mb-2">Información Importante</p>
                      <p className="text-xs text-amber-800 leading-relaxed">
                        Esta simulación es referencial y no constituye una oferta vinculante. Los montos, tasas y condiciones finales están sujetos a evaluación crediticia y aprobación. Las tasas pueden variar según el perfil del solicitante y las condiciones del mercado.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/solicitar-credito" className="flex-1 py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-all text-center whitespace-nowrap">
                    Solicitar Este Crédito
                  </Link>
                  <Link to="/contacto" className="flex-1 py-4 bg-white text-emerald-600 font-semibold rounded-lg hover:bg-slate-50 transition-all border border-emerald-600 text-center whitespace-nowrap">
                    Hablar con un Asesor
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm text-slate-600 mb-4">
              ¿Necesitas ayuda para elegir el crédito adecuado?
            </p>
            <Link to="/servicios" className="text-emerald-600 font-semibold hover:text-emerald-700 transition-colors text-sm">
              Ver Todos los Servicios →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
